import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, Phone, Mail } from "lucide-react";
import { DEFAULT_FAQS_EN } from "./Contact.en";
import { DEFAULT_FAQS_DE } from "./Contact.de";
import { DEFAULT_FAQS_PL } from "./Contact.pl";
import { useResponsive } from "../hooks/useResponsive";
import { colors } from "../styles/colors";
import InteractiveCard from "./InteractiveCard";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { useContent } from "../cms/ContentContext";

const DEFAULT_FAQS_SV = [
  {
    question: "Hur tar jag mig enklast till er i Råstrand, Lappland?",
    answer: [
      "Avståndet från Malmö till vårt hus är cirka 1500 km.",
      "Det snabbaste och smidigaste sättet är flyg. En biljett till Lappland kostar knappt mer än två fulla tankar bensin och rekommenderas starkt.",
      "Vi hämtar och lämnar alltid våra gäster på Skellefteå, Luleå eller Lycksele flygplats."
    ]
  },
  {
    question: "Hur flyger jag enklast till er?",
    answer: [
      "Beroende på var du bor flyger du till Arlanda i Stockholm.",
      "Därifrån tar du inrikesflyg med Amapola till Lycksele (100 km från oss). Transfer ingår i vinterpaketet och biljetter bokas på amapola.nu.",
      "Du kan även flyga till Skellefteå eller Luleå och fortsätta med hyrbil. Dessa flygplatser ligger längre bort men biljetterna kan ibland vara mycket billiga. Vi hjälper dig gärna att hitta de bästa alternativen."
    ]
  },
  {
    question: "Kan jag köra egen bil till er?",
    answer: [
      "Ja, men se till att ha bra vinterdäck. Kör försiktigt då det finns många vilda djur på vägarna.",
      "Från Stockholm till Råstrand tar det 9–10 timmar med bil.",
      "Du hittar Råstrand lättast genom att söka efter 'Cold Experience Lapland' på Google Maps."
    ]
  },
  {
    question: "Ingår snöskoterkläder, kängor och hjälmar?",
    answer: [
      "Ja, vi tillhandahåller ytterkläderna. Snöskoterkläderna är varma – du bär termounderkläder och mjuka kläder under."
    ]
  },
  {
    question: "Vilka kläder ska jag ta med mig?",
    answer: [
      "Det här rekommenderar vi att du packar:",
      "1. Termounderkläder av hög kvalitet",
      "2. Vanliga strumpor",
      "3. Tröja eller fleecejacka",
      "4. Varma fleecebyxor / mjuka byxor",
      "5. Varma ullstrumpor",
      "6. Vinterjacka eller skidställ",
      "7. Vinterskor / kängor",
      "8. Mössa och balaklava att bära under hjälmen",
      "9. Halsduk",
      "10. Bra handskar",
      "11. Solglasögon eller skidglasögon",
      "12. Badkläder",
      "Punkt 6 och 7 används utomhus när du inte kör snöskoter. På snöskoterturer får du overall, skor och hjälm.",
      "I Sverige tar man av sig skorna inomhus, så ta gärna med varma tofflor eller tjocka strumpor för inomhusbruk."
    ]
  },
  {
    question: "Är det något jag inte behöver ta med?",
    answer: [
      "Vi står för sängkläder, handdukar, badrockar och tofflor.",
      "Hårtork finns i badrummet.",
      "Kaffe, te, vatten och juice ingår i helpensionen."
    ]
  },
  {
    question: "Vad ingår inte i helpensionen?",
    answer: [
      "Alkoholdrycker ingår inte. Ta med kontanter om du vill ha det."
    ]
  },
  {
    question: "Behöver jag extra försäkring?",
    answer: [
      "Ja, köp reseförsäkring som täcker äventyrsaktiviteter.",
      "Kontrollera även att din sjukförsäkring gäller utomlands och ta med bevis på försäkringen."
    ]
  },
  {
    question: "Måste jag ha särskilt körkort för snöskoter?",
    answer: [
      "Nej, vanligt körkort räcker.",
      "Alla förare måste ha körkort med sig. Eftersom du alltid kör tillsammans med vår guide är det tillräckligt att guiden har alla nödvändiga tillstånd.",
      "Kontrollera också att ditt pass eller ID är giltigt."
    ]
  },
  {
    question: "Kan jag använda utländsk valuta i Sverige?",
    answer: [
      "Nej, du behöver växla till svenska kronor (SEK) för att handla hos oss eller i byns butik.",
      "De flesta andra butiker tar VISA och Mastercard."
    ]
  }
];

export const formatFaqSegments = (answerLines = []) => {
  const segments = [];
  let currentList = [];

  answerLines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }

    if (/^\d+\./.test(trimmed)) {
      currentList.push(trimmed.replace(/^\d+\.\s*/, ""));
      return;
    }

    if (currentList.length) {
      segments.push({ type: "list", items: currentList });
      currentList = [];
    }

    segments.push({ type: "text", content: trimmed });
  });

  if (currentList.length) {
    segments.push({ type: "list", items: currentList });
  }

  return segments;
};

export const getLocalizedFaqs = (language = "sv") => {
  if (language.startsWith("en")) {
    return DEFAULT_FAQS_EN;
  }

  if (language.startsWith("de")) {
    return DEFAULT_FAQS_DE;
  }

  if (language.startsWith("pl")) {
    return DEFAULT_FAQS_PL;
  }

  return DEFAULT_FAQS_SV;
};

const FaqSection = ({ showCta = true }) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();

  const faqs = useMemo(
    () => getLocalizedFaqs(i18n?.language || "sv"),
    [i18n?.language]
  );

  return (
    <section
      data-cms-section="faq"
      data-cms-page="contact"
      className={isMobile ? "py-12" : "py-20"}
      style={{ background: colors.gradients.heroSolid }}
    >
      <div
        className={`mx-auto ${
          isMobile ? "max-w-sm px-4" : "max-w-4xl px-4 sm:px-6 lg:px-8"
        }`}
      >
        <div className={`text-center ${isMobile ? "mb-8" : "mb-16"}`}>
          <h2
            className={`font-bold mb-6 ${isMobile ? "text-3xl" : "text-4xl"}`}
            style={{ color: colors.text.primary }}
          >
            {t("contact.faqTitle1", "Vanliga")}
            <span style={{ color: colors.accent.primary }}>
              {" "}
              {t("contact.faqTitle2", "Frågor")}
            </span>
          </h2>
          <p
            className={`${isMobile ? "text-lg" : "text-xl"}`}
            style={{ color: colors.text.primary }}
          >
            {t(
              "contact.faqSubtitle",
              "Här hittar du svar på de vanligaste frågorna om våra upplevelser"
            )}
          </p>
        </div>

        <Accordion type="multiple" className="space-y-6">
          {faqs.map((faq, index) => {
            const segments = formatFaqSegments(faq.answer);

            return (
              <AccordionItem
                key={`faq-${index}`}
                value={`faq-${index}`}
                className="border-none"
              >
                <InteractiveCard variant="glass" className="p-0" contentClassName="p-0">
                  <AccordionTrigger
                    className={`${
                      isMobile ? "px-4 py-4" : "px-6 py-5"
                    } text-left text-base md:text-lg font-semibold`}
                    style={{ color: colors.text.primary }}
                  >
                    <div className="flex w-full items-center gap-4 text-left">
                      <div
                        className={`${
                          isMobile ? "h-8 w-8 text-sm" : "h-10 w-10 text-base"
                        } flex items-center justify-center rounded-full font-semibold`}
                        style={{
                          backgroundColor: colors.accent.primary + "20",
                          color: colors.accent.primary,
                        }}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1">
                        <p
                          className={`${
                            isMobile ? "text-base" : "text-lg"
                          } font-semibold leading-snug`}
                          style={{ color: colors.text.primary }}
                        >
                          {faq.question}
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent
                    className={`${
                      isMobile ? "px-4 pb-4" : "px-6 pb-6"
                    } space-y-3`}
                    style={{ color: colors.text.primary }}
                  >
                    {index === 4 ? (
                      <Dialog>
                        <div className="flex flex-col md:flex-row gap-6 items-start mt-2">
                          <div className="flex-1">
                            {segments.map((segment, segmentIndex) =>
                              segment.type === "list" ? (
                                <ol
                                  key={`faq-${index}-segment-${segmentIndex}`}
                                  className="ml-5 list-decimal space-y-1 text-sm md:text-base mb-4"
                                >
                                  {segment.items.map((item, itemIndex) => (
                                    <li
                                      key={`faq-${index}-segment-${segmentIndex}-item-${itemIndex}`}
                                    >
                                      {item}
                                    </li>
                                  ))}
                                </ol>
                              ) : (
                                <p
                                  key={`faq-${index}-segment-${segmentIndex}`}
                                  className="text-sm md:text-base leading-relaxed mb-3"
                                >
                                  {segment.content}
                                </p>
                              )
                            )}
                          </div>
                          <div className="flex-1 flex justify-center md:justify-end mt-6 md:mt-0">
                            <DialogTrigger asChild>
                              <div
                                role="button"
                                tabIndex={0}
                                aria-label="Expand clothes image"
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "inline-block",
                                }}
                              >
                                <img
                                  src={getMedia('faq.clothesImage', '/images/Nya_bilder/clothes.webp')}
                                  alt="How to dress in layers for Lapland"
                                  loading="lazy"
                                  decoding="async"
                                  style={{
                                    maxWidth: 320,
                                    width: "100%",
                                    borderRadius: 12,
                                    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
                                    transition: "box-shadow 0.2s",
                                    outline: "none",
                                    boxSizing: "border-box",
                                    border: "2px solid transparent",
                                  }}
                                  className="hover:shadow-lg hover:border-blue-400 focus:border-blue-600"
                                />
                              </div>
                            </DialogTrigger>
                          </div>
                        </div>
                        <DialogContent className="flex flex-col items-center p-4">
                          <img
                            src={getMedia('faq.clothesImage', '/images/Nya_bilder/clothes.webp')}
                            alt="How to dress in layers for Lapland"
                            loading="lazy"
                            decoding="async"
                            style={{
                              maxWidth: 900,
                              width: "100%",
                              borderRadius: 16,
                              boxShadow: "0 4px 32px rgba(0,0,0,0.18)",
                            }}
                          />
                        </DialogContent>
                      </Dialog>
                    ) : (
                      segments.map((segment, segmentIndex) =>
                        segment.type === "list" ? (
                          <ol
                            key={`faq-${index}-segment-${segmentIndex}`}
                            className="ml-5 list-decimal space-y-1 text-sm md:text-base"
                          >
                            {segment.items.map((item, itemIndex) => (
                              <li
                                key={`faq-${index}-segment-${segmentIndex}-item-${itemIndex}`}
                              >
                                {item}
                              </li>
                            ))}
                          </ol>
                        ) : (
                          <p
                            key={`faq-${index}-segment-${segmentIndex}`}
                            className="text-sm md:text-base leading-relaxed"
                          >
                            {segment.content}
                          </p>
                        )
                      )
                    )}
                  </AccordionContent>
                </InteractiveCard>
              </AccordionItem>
            );
          })}
        </Accordion>

        {showCta && (
          <div className={`text-center ${isMobile ? "mt-8" : "mt-12"}`}>
            <InteractiveCard variant="glass" className={isMobile ? "p-6" : "p-8"}>
              <div className="flex items-center justify-center gap-3 mb-4">
                <CheckCircle
                  className={`${isMobile ? "h-6 w-6" : "h-8 w-8"}`}
                  style={{ color: colors.accent.primary }}
                />
                <h3
                  className={`font-bold ${isMobile ? "text-xl" : "text-2xl"}`}
                  style={{ color: colors.text.primary }}
                >
                  {t("contact.ctaTitle", "Redo att boka din upplevelse?")}
                </h3>
              </div>
              <p
                className={`mb-6 ${isMobile ? "text-sm" : "text-lg"}`}
                style={{ color: colors.text.primary }}
              >
                {t(
                  "contact.ctaSubtitle",
                  "Kontakta oss idag för att skapa minnen som varar livet ut"
                )}
              </p>
              <div
                className={`flex gap-4 ${
                  isMobile ? "flex-col" : "flex-row justify-center"
                }`}
              >
                <a
                  href="tel:+46731814568"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 ${
                    isMobile ? "w-full" : ""
                  }`}
                  style={{
                    backgroundColor: colors.accent.primary,
                    color: "white",
                  }}
                >
                  <Phone className="h-5 w-5" />
                  {t("contact.callNow", "Ring nu")}
                </a>
                <a
                  href="mailto:info@coldexperience.se"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 border-2 ${
                    isMobile ? "w-full" : ""
                  }`}
                  style={{
                    borderColor: colors.accent.primary,
                    color: colors.accent.primary,
                    backgroundColor: "transparent",
                  }}
                >
                  <Mail className="h-5 w-5" />
                  {t("contact.sendEmail", "Skicka e-post")}
                </a>
              </div>
            </InteractiveCard>
          </div>
        )}
      </div>
    </section>
  );
};

export default FaqSection;
